import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField,
    Divider, IconButton, Alert, CircularProgress, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Chip,
    useTheme, useMediaQuery
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../services/api';
import { confirmAcao, toastSuccess } from '../services/alerts';
import { mostrarErro } from '../services/erros';
import { buscarCep, cepCompleto, mensagemFalhaCep } from '../services/cep';
import { startOfDay } from 'date-fns';

export default function PsicologoCadastro() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState('');

    // Responsividade
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // --- ESTADOS PARA OS MODAIS ---
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [alertaCrpVencido, setAlertaCrpVencido] = useState({ open: false, registros: [] });

    const [psicologo, setPsicologo] = useState({
        id_psicologo: null,
        nomeCompleto: '',
        cpf: '',
        rg: '',
        email: '',
        telefone: '',
        dataNascimento: '',
        registrosCRP: [],
        endereco: {
            cep: '',
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            cidade: '',
            uf: ''
        }
    });

    // --- BUSCAR DADOS AO CARREGAR O ECRÃ ---
    useEffect(() => {
        const carregarPerfil = async () => {
            try {
                const response = await api.get('/psicologos');
                if (response.data && response.data.length > 0) {
                    const dados = response.data[0];

                    const hoje = startOfDay(new Date());
                    const vencidos = (dados.registrosCRP || []).filter(crp => {
                        if (!crp.dataValidade) return false;
                        const [ano, mes, dia] = crp.dataValidade.substring(0, 10).split('-');
                        return new Date(ano, mes - 1, dia) < hoje;
                    });
                    if (vencidos.length > 0) {
                        setAlertaCrpVencido({ open: true, registros: vencidos });
                    }

                    setPsicologo({
                        id_psicologo: dados.id_psicologo,
                        nomeCompleto: dados.nomeCompleto || '',
                        cpf: dados.cpf ? formatarCPF(dados.cpf) : '',
                        rg: dados.rg ? formatarRG(dados.rg) : '',
                        email: dados.email || '',
                        telefone: dados.telefone ? formatarTelefone(dados.telefone) : '',
                        dataNascimento: dados.dataNascimento ? formatarDataInput(dados.dataNascimento) : '',
                        registrosCRP: dados.registrosCRP ? dados.registrosCRP.map(crp => ({
                            ...crp,
                            numero: formatarNumeroCRP(crp.numero || ''),
                            regiaoUf: formatarRegiaoUF(crp.regiaoUf || ''),
                            dataEmissao: crp.dataEmissao ? formatarDataInput(crp.dataEmissao) : '',
                            dataValidade: crp.dataValidade ? formatarDataInput(crp.dataValidade) : ''
                        })) : [],
                        endereco: dados.endereco ? {
                            cep: formatarCEP(dados.endereco.cep || ''),
                            logradouro: dados.endereco.logradouro || '',
                            numero: dados.endereco.numero || '',
                            complemento: dados.endereco.complemento || '',
                            bairro: dados.endereco.bairro || '',
                            cidade: dados.endereco.cidade?.nome || '',
                            uf: dados.endereco.cidade?.estado?.sigla || dados.endereco.cidade?.estado?.sigla || ''                        } : { cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' }
                    });
                }
            } catch (err) {
                console.error("Erro ao buscar dados do perfil", err);
                setError('Não foi possível carregar os dados do perfil.');
            } finally {
                setFetching(false);
            }
        };

        carregarPerfil();
    }, []);

    // --- FUNÇÕES DE FORMATAÇÃO E VALIDAÇÃO DE DATA ---
    const formatarCPF = (valor) => {
        if (!valor) return '';
        return valor.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})/, '$1-$2').replace(/(-\d{2})\d+?$/, '$1');
    };

    const formatarRG = (valor) => {
        if (!valor) return '';
        let v = valor.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        if (v.length > 2) v = v.substring(0, 2) + '.' + v.substring(2);
        if (v.length > 6) v = v.substring(0, 6) + '.' + v.substring(6);
        if (v.length > 10) v = v.substring(0, 10) + '-' + v.substring(10, 11);
        return v.substring(0, 12);
    };

    const formatarTelefone = (valor) => {
        if (!valor) return '';
        return valor.replace(/\D/g, '').replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4,5})(\d{4})/, '$1-$2').replace(/(-\d{4})\d+?$/, '$1');
    };

    const formatarNumeroCRP = (valor) => {
        if (!valor) return '';
        let v = valor.replace(/\D/g, '');
        if (v.length > 2) v = v.substring(0, 2) + '/' + v.substring(2, 8);
        return v;
    };

    const formatarRegiaoUF = (valor) => {
        if (!valor) return '';
        let v = valor.toUpperCase().replace(/[^0-9A-Z]/g, '');
        if (v.length > 2) v = v.substring(0, 2) + '/' + v.substring(2, 4).replace(/[^A-Z]/g, '');
        return v.substring(0, 5);
    };

    const formatarCEP = (valor) => {
        if (!valor) return '';
        return valor.replace(/\D/g, '').replace(/^(\d{5})(\d)/, '$1-$2').substring(0, 9);
    };

    const formatarDataInput = (dataString) => {
        if (!dataString) return '';
        if (dataString.length > 10) {
            return dataString.substring(0, 10);
        }
        return dataString;
    };

    const verificarCrpVencido = (dataValidade) => {
        if (!dataValidade) return false;
        const [ano, mes, dia] = dataValidade.split('-');
        const dataVencimento = new Date(ano, mes - 1, dia);
        const hoje = startOfDay(new Date());
        return dataVencimento < hoje;
    };

    // Consulta via services/cep.js: lá ficam o timeout, o { erro: true } que o
    // ViaCEP devolve com HTTP 200 e as mensagens por motivo de falha. O retorno
    // continua indo para o snackbar desta tela, que não usa toast.
    const handleBuscaCEP = async (event) => {
        if (!cepCompleto(event.target.value)) return;
        const resultado = await buscarCep(event.target.value);
        if (!resultado.ok) {
            setSnackbar({
                open: true,
                message: mensagemFalhaCep(resultado.motivo),
                severity: resultado.motivo === 'nao_encontrado' ? 'warning' : 'error',
            });
            return;
        }
        setPsicologo(prev => ({
            ...prev,
            endereco: {
                ...prev.endereco,
                logradouro: resultado.endereco.logradouro,
                bairro: resultado.endereco.bairro,
                cidade: resultado.endereco.localidade,
                uf: resultado.endereco.uf,
            }
        }));
    };

    // --- VALIDAÇÕES ---
    const validarDados = () => {
        if (!psicologo.nomeCompleto.trim()) return 'O Nome Completo é obrigatório.';
        if (psicologo.cpf.replace(/\D/g, '').length !== 11) return 'CPF inválido.';

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (psicologo.email && !emailRegex.test(psicologo.email)) return 'E-mail inválido.';

        const telLength = psicologo.telefone.replace(/\D/g, '').length;
        if (telLength < 10) return 'Telefone inválido.';

        if (psicologo.registrosCRP.length === 0) return 'É necessário adicionar ao menos um registo de CRP.';

        for (let i = 0; i < psicologo.registrosCRP.length; i++) {
            const crp = psicologo.registrosCRP[i];
            if (!crp.numero || !crp.regiaoUf || !crp.tipoInscricao) {
                return `Preencha os campos obrigatórios do CRP #${i + 1}.`;
            }
            if (crp.regiaoUf.length < 5 || !crp.regiaoUf.includes('/')) {
                return `O formato da Região/UF do CRP #${i + 1} deve ser "00/UF". Ex: 08/PR.`;
            }
            if (crp.dataEmissao && crp.dataValidade) {
                if (new Date(crp.dataValidade) < new Date(crp.dataEmissao)) {
                    return `A Data de Validade do CRP #${i + 1} não pode ser anterior à Data de Emissão.`;
                }
            }
        }

        const end = psicologo.endereco;
        if (end.cep && (!end.logradouro || !end.numero || !end.bairro || !end.cidade || !end.uf)) {
            return 'Por favor, preencha todos os campos obrigatórios do Endereço (Logradouro, Número, Bairro, Cidade e UF).';
        }

        return null;
    };

    // --- GESTÃO DE CRP ---
    const handleAdicionarCRP = () => {
        setPsicologo({
            ...psicologo,
            registrosCRP: [
                ...psicologo.registrosCRP,
                { id_registro: null, numero: '', regiaoUf: '', tipoInscricao: '', dataEmissao: '', dataValidade: '', status: true }
            ]
        });
    };

    const solicitarRemocaoCRP = async (index) => {
        const crp = psicologo.registrosCRP[index];
        // Registro ainda não gravado sai sem confirmação: não há o que perder.
        if (!crp?.id_registro) {
            const novosCRPs = [...psicologo.registrosCRP];
            novosCRPs.splice(index, 1);
            setPsicologo({ ...psicologo, registrosCRP: novosCRPs });
            return;
        }

        const ok = await confirmAcao({
            title: 'Excluir registro de CRP?',
            text: `O registro ${crp.numero || ''} deixa de constar no cadastro e nos documentos emitidos a partir de agora.`,
            confirmButtonText: 'Excluir',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        try {
            await api.delete(`/registros-crp/${crp.id_registro}`, { skipGlobalError: true });
            const novosCRPs = [...psicologo.registrosCRP];
            novosCRPs.splice(index, 1);
            setPsicologo({ ...psicologo, registrosCRP: novosCRPs });
            toastSuccess('CRP excluído.');
        } catch (error) {
            // A lista não é alterada: o registro continua no servidor.
            mostrarErro(error, 'excluir o registro de CRP');
        }
    };

    const handleCRPChange = (index, campo, valor) => {
        let formattedValue = valor;
        if (campo === 'numero') formattedValue = formatarNumeroCRP(valor);
        if (campo === 'regiaoUf') formattedValue = formatarRegiaoUF(valor);

        const novosCRPs = [...psicologo.registrosCRP];
        novosCRPs[index][campo] = formattedValue;
        setPsicologo({ ...psicologo, registrosCRP: novosCRPs });
    };

    // --- SALVAR ---
    const solicitarSalvar = async () => {
        setError('');
        const erroMsg = validarDados();
        if (erroMsg) {
            setError(erroMsg);
            return;
        }
        // O aviso de CRP vencido vinha no corpo do diálogo antigo; mantido aqui,
        // porque é o que o profissional precisa ver antes de gravar.
        const temCrpVencido = psicologo.registrosCRP.some(crp => verificarCrpVencido(crp.dataValidade));
        const ok = await confirmAcao({
            title: 'Confirmar alterações',
            text: 'Deseja gravar os dados do seu perfil profissional?'
                + (temCrpVencido ? ' Atenção: você possui um ou mais registros de CRP vencidos.' : ''),
            confirmButtonText: 'Confirmar e salvar',
            cancelButtonText: 'Rever dados',
            icon: temCrpVencido ? 'warning' : 'question',
        });
        if (ok) executarSalvar();
    };

    const executarSalvar = async () => {
        setLoading(true);

        try {
            const psicologoParaSalvar = {
                ...psicologo,
                cpf: psicologo.cpf.replace(/\D/g, ''),
                telefone: psicologo.telefone.replace(/\D/g, ''),
                dataNascimento: psicologo.dataNascimento || null,
                registrosCRP: psicologo.registrosCRP.map(crp => ({
                    ...crp,
                    dataEmissao: crp.dataEmissao || null,
                    dataValidade: crp.dataValidade || null
                })),
                endereco: psicologo.endereco.cep ? {
                    cep: psicologo.endereco.cep.replace(/\D/g, ''),
                    logradouro: psicologo.endereco.logradouro,
                    numero: psicologo.endereco.numero,
                    complemento: psicologo.endereco.complemento,
                    bairro: psicologo.endereco.bairro,
                    nomeCidade: psicologo.endereco.cidade,
                    ufEstado: psicologo.endereco.uf
                } : null
            };

            let mensagemSucesso = '';
            if (psicologo.id_psicologo) {
                await api.put(`/psicologos/${psicologo.id_psicologo}`, psicologoParaSalvar);
                mensagemSucesso = 'Perfil profissional atualizado com sucesso!';
            } else {
                await api.post('/psicologos', psicologoParaSalvar);
                mensagemSucesso = 'Perfil profissional cadastrado com sucesso!';
            }

            setSnackbar({ open: true, message: mensagemSucesso, severity: 'success' });

            const response = await api.get('/psicologos');
            if (response.data && response.data.length > 0) {
                const dados = response.data[0];
                setPsicologo({
                    id_psicologo: dados.id_psicologo,
                    nomeCompleto: dados.nomeCompleto || '',
                    cpf: dados.cpf ? formatarCPF(dados.cpf) : '',
                    rg: dados.rg ? formatarRG(dados.rg) : '',
                    email: dados.email || '',
                    telefone: dados.telefone ? formatarTelefone(dados.telefone) : '',
                    dataNascimento: dados.dataNascimento ? formatarDataInput(dados.dataNascimento) : '',
                    registrosCRP: dados.registrosCRP ? dados.registrosCRP.map(crp => ({
                        ...crp,
                        numero: formatarNumeroCRP(crp.numero || ''),
                        regiaoUf: formatarRegiaoUF(crp.regiaoUf || ''),
                        dataEmissao: crp.dataEmissao ? formatarDataInput(crp.dataEmissao) : '',
                        dataValidade: crp.dataValidade ? formatarDataInput(crp.dataValidade) : ''
                    })) : [],
                    endereco: dados.endereco ? {
                        cep: formatarCEP(dados.endereco.cep || ''),
                        logradouro: dados.endereco.logradouro || '',
                        numero: dados.endereco.numero || '',
                        complemento: dados.endereco.complemento || '',
                        bairro: dados.endereco.bairro || '',
                        cidade: dados.endereco.cidade?.nome || '',
                        uf: dados.endereco.cidade?.estado?.uf || ''
                    } : { cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' }
                });
            }

        } catch (err) {
            console.error(err);
            if (err.response && err.response.data && typeof err.response.data === 'string') {
                setError(err.response.data);
            } else {
                setError('Erro ao salvar os dados. Verifique a ligação com o servidor.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, px: isMobile ? 1 : 0 }}>
            {/* CABEÇALHO */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexDirection: 'row' }}>
                <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                    Perfil Profissional
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>

                    {/* COLUNA 1: DADOS PESSOAIS + ENDEREÇO */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>


                        {/* --- DADOS PESSOAIS --- */}
                        <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
                            Dados Pessoais
                        </Typography>

                        <TextField fullWidth label="Nome Completo" required value={psicologo.nomeCompleto} onChange={(e) => setPsicologo({...psicologo, nomeCompleto: e.target.value})} />

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField fullWidth label="CPF" required value={psicologo.cpf} onChange={(e) => setPsicologo({...psicologo, cpf: formatarCPF(e.target.value)})} inputProps={{ maxLength: 14 }} />
                            <TextField fullWidth label="RG" required value={psicologo.rg} onChange={(e) => setPsicologo({...psicologo, rg: formatarRG(e.target.value)})} inputProps={{ maxLength: 12 }} />
                        </Box>

                        <TextField fullWidth label="E-mail" type="email" value={psicologo.email} onChange={(e) => setPsicologo({...psicologo, email: e.target.value})} />

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField fullWidth label="Telefone" required value={psicologo.telefone} onChange={(e) => setPsicologo({...psicologo, telefone: formatarTelefone(e.target.value)})} inputProps={{ maxLength: 15 }} />
                            <TextField fullWidth label="Data Nascimento" type="date" InputLabelProps={{ shrink: true }} value={psicologo.dataNascimento} onChange={(e) => setPsicologo({...psicologo, dataNascimento: e.target.value})} />
                        </Box>

                        {/* --- ADICIONADO: SESSÃO ENDEREÇO (MANTENDO A ESTRUTURA DA COLUNA 1) --- */}
                        <Divider sx={{ my: 1 }} />

                        <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
                            Endereço
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                fullWidth
                                label="CEP"
                                value={psicologo.endereco.cep}
                                onChange={(e) => setPsicologo({ ...psicologo, endereco: { ...psicologo.endereco, cep: formatarCEP(e.target.value) } })}
                                onBlur={handleBuscaCEP} // ViaCEP dispara aqui
                                inputProps={{ maxLength: 9 }}
                                helperText="Digite para buscar"
                            />
                            <TextField
                                fullWidth
                                label="Bairro"
                                required={!!psicologo.endereco.cep}
                                value={psicologo.endereco.bairro}
                                onChange={(e) => setPsicologo({ ...psicologo, endereco: { ...psicologo.endereco, bairro: e.target.value } })}
                            />
                        </Box>

                        <TextField
                            fullWidth
                            label="Logradouro"
                            required={!!psicologo.endereco.cep}
                            value={psicologo.endereco.logradouro}
                            onChange={(e) => setPsicologo({ ...psicologo, endereco: { ...psicologo.endereco, logradouro: e.target.value } })}
                        />

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Número"
                                required={!!psicologo.endereco.cep}
                                value={psicologo.endereco.numero}
                                onChange={(e) => setPsicologo({ ...psicologo, endereco: { ...psicologo.endereco, numero: e.target.value } })}
                            />
                            <TextField
                                fullWidth
                                label="Complemento"
                                placeholder="Sala, Bloco"
                                value={psicologo.endereco.complemento}
                                onChange={(e) => setPsicologo({ ...psicologo, endereco: { ...psicologo.endereco, complemento: e.target.value } })}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Cidade"
                                required={!!psicologo.endereco.cep}
                                value={psicologo.endereco.cidade}
                                onChange={(e) => setPsicologo({ ...psicologo, endereco: { ...psicologo.endereco, cidade: e.target.value } })}
                            />
                            <TextField
                                fullWidth
                                label="UF"
                                required={!!psicologo.endereco.cep}
                                inputProps={{ maxLength: 2 }}
                                value={psicologo.endereco.uf}
                                onChange={(e) => setPsicologo({ ...psicologo, endereco: { ...psicologo.endereco, uf: e.target.value.toUpperCase() } })}
                            />
                        </Box>

                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                    {/* COLUNA 2: DADOS DO CRP */}
                    <Box sx={{ flex: 1.2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
                                Registos de CRP
                            </Typography>
                            <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={handleAdicionarCRP} sx={{ color: '#1ABB9C', borderColor: '#1ABB9C' }}>
                                Adicionar
                            </Button>
                        </Box>

                        {psicologo.registrosCRP.length === 0 ? (
                            <Box sx={{ p: 4, border: '2px dashed #eee', borderRadius: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">Nenhum registo profissional adicionado.</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {psicologo.registrosCRP.map((crp, index) => {
                                    const isVencido = verificarCrpVencido(crp.dataValidade);

                                    return (
                                        <Paper key={index} variant="outlined" sx={{ p: 2, bgcolor: isVencido ? '#fff5f5' : '#fafafa', position: 'relative', borderColor: isVencido ? '#ffcdd2' : 'rgba(0, 0, 0, 0.12)' }}>
                                            <IconButton size="small" color="error" onClick={() => solicitarRemocaoCRP(index)} sx={{ position: 'absolute', top: 5, right: 5 }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                                {isVencido && (
                                                    <Box sx={{ mb: -1 }}>
                                                        <Chip icon={<WarningAmberIcon />} label="CRP Vencido" color="error" size="small" variant="filled" />
                                                    </Box>
                                                )}

                                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                                    <TextField fullWidth size="small" label="Número CRP" placeholder="00/00000" required value={crp.numero} onChange={(e) => handleCRPChange(index, 'numero', e.target.value)} inputProps={{ maxLength: 9 }} />
                                                    <TextField fullWidth size="small" label="Região/UF" placeholder="08/PR" required value={crp.regiaoUf} onChange={(e) => handleCRPChange(index, 'regiaoUf', e.target.value)} />
                                                </Box>

                                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                                    <TextField fullWidth size="small" select label="Inscrição" required value={crp.tipoInscricao} onChange={(e) => handleCRPChange(index, 'tipoInscricao', e.target.value)}>
                                                        <MenuItem value="Principal">Principal</MenuItem>
                                                        <MenuItem value="Secundária">Secundária</MenuItem>
                                                        <MenuItem value="Provisória">Provisória</MenuItem>
                                                    </TextField>
                                                    <TextField fullWidth size="small" type="date" label="Emissão" InputLabelProps={{ shrink: true }} value={crp.dataEmissao} onChange={(e) => handleCRPChange(index, 'dataEmissao', e.target.value)} />
                                                    <TextField fullWidth size="small" type="date" label="Validade" InputLabelProps={{ shrink: true }} value={crp.dataValidade} onChange={(e) => handleCRPChange(index, 'dataValidade', e.target.value)} error={isVencido} />
                                                </Box>
                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Botões ajustados para Mobile (Empilham e ficam 100% da largura) */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" fullWidth={isMobile} onClick={() => navigate(-1)} sx={{ color: '#666', borderColor: '#ccc' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        fullWidth={isMobile}
                        onClick={solicitarSalvar}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: { sm: 5 } }}
                    >
                        {psicologo.id_psicologo ? 'Atualizar Perfil' : 'Salvar Perfil'}
                    </Button>
                </Box>
            </Paper>



            <Dialog fullScreen={isMobile} open={alertaCrpVencido.open} onClose={() => setAlertaCrpVencido({ open: false, registros: [] })}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f', mt: isMobile ? 2 : 0 }}>
                    <WarningAmberIcon /> Atenção: CRP Vencido
                </DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        {alertaCrpVencido.registros.length === 1
                            ? 'Você possui um registro de CRP vencido:'
                            : `Você possui ${alertaCrpVencido.registros.length} registros de CRP vencidos:`}
                        <Box component="ul" sx={{ mt: 1, mb: 1 }}>
                            {alertaCrpVencido.registros.map((crp, i) => (
                                <li key={i}>
                                    <strong>{formatarNumeroCRP(crp.numero || '')}</strong>
                                    {crp.regiaoUf ? ` — ${formatarRegiaoUF(crp.regiaoUf)}` : ''}
                                    {crp.dataValidade ? ` (validade: ${crp.dataValidade.substring(0, 10).split('-').reverse().join('/')})` : ''}
                                </li>
                            ))}
                        </Box>
                        Atualize a data de validade para manter seu cadastro regular.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button fullWidth={isMobile} onClick={() => setAlertaCrpVencido({ open: false, registros: [] })} variant="contained" sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}>
                        Entendi
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}